/**
 * A ringtone with no audio file behind it.
 *
 * Two tones a fifth apart, on for a second and off for two, the cadence a
 * phone has used for a century. Generated so the client ships no sound asset
 * and so the tone stops the instant the call is answered, declined or ended:
 * a ring that carries on after that is worse than none.
 */

export interface Ringtone {
  stop: () => void
}

/**
 * The ringtone proper: the same eight-second file the phones ring with,
 * served from the site and looped. Falls back to the generated tone when
 * the element cannot play — an autoplay refusal, or no audio output — so a
 * call never rings silently for want of a file.
 */
export function startRingtone(): Ringtone | null {
  if (typeof Audio === 'undefined') return startGeneratedRingtone()

  let audio: HTMLAudioElement
  try {
    audio = new Audio('/incoming_call.wav')
  } catch {
    return startGeneratedRingtone()
  }
  audio.loop = true
  audio.volume = 0.6

  let fallback: Ringtone | null = null
  let stopped = false
  void audio.play().catch(() => {
    if (!stopped) fallback = startGeneratedRingtone()
  })

  return {
    stop: () => {
      stopped = true
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
      fallback?.stop()
    },
  }
}

function startGeneratedRingtone(): Ringtone | null {
  const AudioContextImpl =
    typeof AudioContext !== 'undefined'
      ? AudioContext
      : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (AudioContextImpl === undefined) return null

  let context: AudioContext
  try {
    context = new AudioContextImpl()
  } catch {
    return null
  }

  const gain = context.createGain()
  gain.gain.value = 0
  gain.connect(context.destination)

  const low = context.createOscillator()
  const high = context.createOscillator()
  low.type = 'sine'
  high.type = 'sine'
  low.frequency.value = 440
  high.frequency.value = 660
  low.connect(gain)
  high.connect(gain)
  low.start()
  high.start()

  // One second on, two off. Volume ramps rather than snaps so the tone does
  // not click.
  let stopped = false
  const ring = (): void => {
    if (stopped) return
    const now = context.currentTime
    gain.gain.cancelScheduledValues(now)
    gain.gain.setValueAtTime(0, now)
    gain.gain.linearRampToValueAtTime(0.08, now + 0.05)
    gain.gain.setValueAtTime(0.08, now + 0.95)
    gain.gain.linearRampToValueAtTime(0, now + 1)
  }
  ring()
  const timer = setInterval(ring, 3000)

  // Browsers refuse to start audio without a gesture; a ring that cannot
  // sound fails quietly rather than throwing over the call banner.
  void context.resume().catch(() => undefined)

  return {
    stop: () => {
      if (stopped) return
      stopped = true
      clearInterval(timer)
      try {
        low.stop()
        high.stop()
      } catch {
        // Already stopped.
      }
      void context.close().catch(() => undefined)
    },
  }
}
